import enMessages from '../../../messages/en.json';
import zhMessages from '../../../messages/zh.json';
import { buildProductIdeaHref, PRODUCT_IDEAS_PATH, readProductIdea } from '../routing/product-idea-entry';
import { guideVisual } from './guide-visuals';
import { CREATIVE_CASE_MEDIA } from './creative-case-media';
import { buildLocalizedSeoPath } from './site';
import type { SeoGuideArticle, SeoGuideLocale, SeoGuideSection } from './guides';

export function buildProductIdeasGuide(locale: SeoGuideLocale): SeoGuideArticle {
  const copy = (locale === 'zh' ? zhMessages : enMessages).seoGuides.productPhotographyIdeas;
  const visuals = copy.visuals;
  const jacket = guideVisual('referenceStyleJacketRevised', visuals.jacket.alt, visuals.jacket.caption);
  const trench = guideVisual('referenceStyleTrench', visuals.trench.alt, visuals.trench.caption);
  const sections: SeoGuideSection[] = copy.sections.map(section => ({ ...section }));
  sections.find(section => section.id === 'soft-studio')!.visuals = [
    guideVisual('referenceProductSource', visuals.source.alt, visuals.source.caption), jacket,
  ];
  sections.find(section => section.id === 'product-detail')!.visuals = [
    { ...CREATIVE_CASE_MEDIA.bag, ...visuals.bag },
    { ...CREATIVE_CASE_MEDIA.outfit, ...visuals.outfit },
  ];
  sections.find(section => section.id === 'style-reuse')!.visuals = [jacket, trench];
  for (const section of sections) {
    const idea = readProductIdea(section.id);
    if (idea) section.links = [{ label: copy.directionCta, href: buildProductIdeaHref(locale, idea) }];
  }
  sections.find(section => section.id === 'your-brief')!.links = [{ label: copy.ctaLabel, href: buildProductIdeaHref(locale) }];
  return {
    slug: 'product-photography-ideas', path: PRODUCT_IDEAS_PATH, priority: 0.73,
    landingPath: '/ai-clothing-photography', title: copy.title, metadataTitle: copy.metadataTitle,
    description: copy.description, primaryKeyword: copy.primaryKeyword, searchIntent: copy.searchIntent,
    readingTime: copy.readingTime, heroImage: jacket.src, heroImageWidth: jacket.width,
    heroImageHeight: jacket.height, heroImageAlt: copy.heroImageAlt,
    images: [...new Set(sections.flatMap(section => section.visuals?.map(visual => visual.src) ?? []))],
    publishedDate: '2026-09-15', modifiedDate: '2026-09-15', startHref: buildProductIdeaHref(locale),
    takeawaysTitle: copy.takeawaysTitle, takeaways: copy.takeaways, sections,
    table: { title: copy.table.title, headers: [copy.table.headers[0], copy.table.headers[1], copy.table.headers[2]],
      rows: copy.table.rows.map(row => [row[0], row[1], row[2]]) },
    checklistTitle: copy.checklistTitle, checklist: copy.checklist, sourceTitle: copy.sourceTitle, sourceEyebrow: copy.sourceEyebrow,
    sources: [
      { label: copy.sourceLabels.style, href: buildLocalizedSeoPath('/guides/reference-image-ai-generation-workflow', locale) },
      { label: copy.sourceLabels.product, href: buildLocalizedSeoPath('/guides/ai-product-photography-workflow', locale) },
    ],
    faq: copy.faq, ctaTitle: copy.ctaTitle, ctaBody: copy.ctaBody, ctaLabel: copy.ctaLabel,
  };
}
